import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { formatRelativeTime, getInitials } from '@/lib/utils';
import { Search, MessageCircle, User, Package, ArrowLeft, X, Send, Megaphone, Loader2 } from 'lucide-react';
import api, { adminAPI } from '@/lib/api';
import { toast } from 'sonner';

const AdminConversationsTab = () => {
    const [searchParams] = useSearchParams();
    const userIdFilter = searchParams.get('user');
    
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [targetUser, setTargetUser] = useState(null);
    
    // Infinite scroll
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const perPage = 30;
    const observerRef = useRef(null);
    const loadMoreRef = useRef(null);
    
    // Broadcast state
    const [broadcastMessage, setBroadcastMessage] = useState('');
    const [broadcastSubject, setBroadcastSubject] = useState('');
    const [broadcastSendEmail, setBroadcastSendEmail] = useState(false);
    const [sendingBroadcast, setSendingBroadcast] = useState(false);
    const [showBroadcastForm, setShowBroadcastForm] = useState(false);

    const fetchConversations = useCallback(async (pageNum = 1, reset = false) => {
        try {
            if (pageNum === 1) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }
            
            // If we have a user filter, fetch that user's conversations
            if (userIdFilter) {
                const userRes = await api.get(`/admin/users/${userIdFilter}/conversations`);
                setConversations(userRes.data.conversations || []);
                setTargetUser(userRes.data.user || null);
                setHasMore(false); // User conversations don't paginate
            } else {
                // Fetch all recent conversations (admin view) with pagination
                const params = new URLSearchParams();
                params.append('skip', ((pageNum - 1) * perPage).toString());
                params.append('limit', perPage.toString());
                
                const res = await api.get(`/admin/conversations?${params.toString()}`);
                const newConversations = res.data?.conversations || res.data || [];
                const total = res.data?.total || newConversations.length;
                
                if (reset || pageNum === 1) {
                    setConversations(newConversations);
                } else {
                    setConversations(prev => [...prev, ...newConversations]);
                }
                
                setTotalCount(total);
                setHasMore(newConversations.length === perPage);
                setTargetUser(null);
            }
        } catch (error) {
            console.error('Error fetching conversations:', error);
            setConversations([]);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [userIdFilter]);

    useEffect(() => {
        setPage(1);
        fetchConversations(1, true);
    }, [userIdFilter]);

    // Load more when page changes
    useEffect(() => {
        if (page > 1) {
            fetchConversations(page, false);
        }
    }, [page]);

    // Setup intersection observer for infinite scroll within ScrollArea
    const handleScroll = useCallback((e) => {
        const target = e.target;
        if (target.scrollHeight - target.scrollTop <= target.clientHeight + 100) {
            if (hasMore && !loadingMore && !loading) {
                setPage(prev => prev + 1);
            }
        }
    }, [hasMore, loadingMore, loading]);

    const fetchMessages = async (conversationId) => {
        try {
            const res = await api.get(`/admin/conversations/${conversationId}/messages`);
            setMessages(res.data || []);
        } catch (error) {
            console.error('Error fetching messages:', error);
            setMessages([]);
        }
    };

    const handleSelectConversation = (conv) => {
        setSelectedConversation(conv);
        fetchMessages(conv.conversation_id);
    };

    const handleSendBroadcast = async () => {
        if (!broadcastMessage.trim()) {
            toast.error('Mesajul nu poate fi gol');
            return;
        }
        
        if (broadcastSendEmail && !broadcastSubject.trim()) {
            toast.error('Subiectul este obligatoriu când trimiți și email');
            return;
        }
        
        setSendingBroadcast(true);
        try {
            const res = await adminAPI.sendBroadcast(broadcastMessage, broadcastSubject || null, broadcastSendEmail);
            let successMsg = `Mesaj trimis către ${res.data.messages_sent} utilizatori`;
            if (broadcastSendEmail) {
                successMsg += `. Email-uri: ${res.data.emails_sent} trimise`;
                if (res.data.emails_failed > 0) {
                    successMsg += `, ${res.data.emails_failed} eșuate`;
                }
            }
            toast.success(successMsg);
            setBroadcastMessage('');
            setBroadcastSubject('');
            setBroadcastSendEmail(false);
            setShowBroadcastForm(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la trimiterea mesajului');
        } finally {
            setSendingBroadcast(false);
        }
    };

    const filteredConversations = conversations.filter(conv => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            conv.participant_names?.some(u => u?.toLowerCase().includes(query)) ||
            conv.participant_usernames?.some(u => u?.toLowerCase().includes(query)) ||
            conv.listing_title?.toLowerCase().includes(query)
        );
    });

    if (loading) {
        return (
            <div className="py-8 text-center text-muted-foreground">
                Se încarcă conversațiile...
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Broadcast Section */}
            <Card className="border-primary/20 bg-primary/5">
                <CardHeader className="py-3 px-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Megaphone className="w-5 h-5 text-primary" />
                            <CardTitle className="text-base font-medium">Mesaj broadcast</CardTitle>
                        </div>
                        <Button 
                            variant={showBroadcastForm ? "outline-solid" : "default"}
                            size="sm"
                            onClick={() => setShowBroadcastForm(!showBroadcastForm)}
                        >
                            {showBroadcastForm ? 'Anulează' : 'Trimite tuturor'}
                        </Button>
                    </div>
                </CardHeader>
                {showBroadcastForm && (
                    <CardContent className="pt-0 space-y-3">
                        <div>
                            <Input
                                placeholder={broadcastSendEmail ? "Subiect (obligatoriu pentru email)" : "Subiect (opțional)"}
                                value={broadcastSubject}
                                onChange={(e) => setBroadcastSubject(e.target.value)}
                                className={`bg-background ${broadcastSendEmail && !broadcastSubject.trim() ? 'border-red-300' : ''}`}
                                required={broadcastSendEmail}
                            />
                        </div>
                        <div>
                            <Textarea
                                placeholder="Scrie mesajul care va fi trimis tuturor utilizatorilor..."
                                value={broadcastMessage}
                                onChange={(e) => setBroadcastMessage(e.target.value)}
                                rows={4}
                                className="bg-background resize-none"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="broadcast-send-email"
                                checked={broadcastSendEmail}
                                onChange={(e) => setBroadcastSendEmail(e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                            />
                            <label htmlFor="broadcast-send-email" className="text-sm font-medium cursor-pointer">
                                Trimite și email (obligatoriu, ignoră preferințele utilizatorilor)
                            </label>
                        </div>
                        <div className="flex items-center justify-between">
                            <p className="text-xs text-muted-foreground">
                                Mesajul va fi trimis tuturor utilizatorilor platformei
                            </p>
                            <Button 
                                onClick={handleSendBroadcast}
                                disabled={sendingBroadcast || !broadcastMessage.trim()}
                                size="sm"
                            >
                                {sendingBroadcast ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Se trimite...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4 mr-2" />
                                        Trimite broadcast
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                )}
            </Card>
            
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold flex items-center gap-2">
                        <MessageCircle className="w-5 h-5" />
                        {targetUser ? (
                            <>Conversații: {targetUser.username || targetUser.name}</>
                        ) : (
                            <>Toate conversațiile</>
                        )}
                    </h2>
                    {targetUser && (
                        <p className="text-sm text-muted-foreground">
                            {targetUser.email}
                        </p>
                    )}
                </div>
                {targetUser && (
                    <Button 
                        variant="outline" 
                        onClick={() => window.location.href = '/admin?tab=users'}
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Înapoi la utilizatori
                    </Button>
                )}
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                    placeholder="Caută conversații..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                />
            </div>

            {/* Conversations List & Messages */}
            <div className={`grid lg:grid-cols-3 gap-4 h-[calc(100vh-220px)] lg:h-[600px]`}>
                {/* Conversations List - hidden on mobile when conversation selected */}
                <Card className={`lg:col-span-1 overflow-hidden ${selectedConversation ? 'hidden lg:flex lg:flex-col' : 'flex flex-col'}`}>
                    <CardHeader className="py-3 px-4 border-b shrink-0">
                        <CardTitle className="text-sm font-medium">
                            Conversații ({totalCount || filteredConversations.length})
                        </CardTitle>
                    </CardHeader>
                    <ScrollArea className="flex-1" onScrollCapture={handleScroll}>
                        {filteredConversations.length === 0 ? (
                            <div className="p-8 text-center text-muted-foreground">
                                <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>Nu există conversații</p>
                            </div>
                        ) : (
                            <div className="divide-y">
                                {filteredConversations.map(conv => (
                                    <button
                                        key={conv.conversation_id}
                                        onClick={() => handleSelectConversation(conv)}
                                        className={`w-full p-3 text-left hover:bg-muted/50 transition-colors ${
                                            selectedConversation?.conversation_id === conv.conversation_id 
                                                ? 'bg-primary/5 border-l-2 border-primary' 
                                                : ''
                                        }`}
                                    >
                                        <div className="flex items-start gap-2">
                                            <div className="flex -space-x-2">
                                                {(conv.participant_names || conv.participant_usernames)?.slice(0, 2).map((name, i) => (
                                                    <Avatar key={i} className="w-8 h-8 border-2 border-white">
                                                        {conv.participant_avatars?.[i] ? (
                                                            <AvatarImage src={conv.participant_avatars[i]} alt={name} />
                                                        ) : null}
                                                        <AvatarFallback className="text-xs bg-primary/10">
                                                            {getInitials(name)}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                ))}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm truncate">
                                                    {(conv.participant_names || conv.participant_usernames)?.join(' ↔ ')}
                                                </p>
                                                {conv.listing_title && (
                                                    <p className="text-xs text-primary truncate flex items-center gap-1">
                                                        <Package className="w-3 h-3" />
                                                        {conv.listing_title}
                                                    </p>
                                                )}
                                                {conv.last_message && (
                                                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                                                        {conv.last_message}
                                                    </p>
                                                )}
                                                <p className="text-xs text-muted-foreground mt-0.5">
                                                    {formatRelativeTime(conv.last_message_at || conv.updated_at)}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                                
                                {/* Infinite scroll loader */}
                                {hasMore && (
                                    <div className="flex justify-center py-3">
                                        {loadingMore && (
                                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </ScrollArea>
                </Card>

                {/* Messages View - full screen on mobile when conversation selected */}
                <Card className={`lg:col-span-2 overflow-hidden flex flex-col h-full ${selectedConversation ? '' : 'hidden lg:flex'}`}>
                    {selectedConversation ? (
                        <>
                            <CardHeader className="py-3 px-4 border-b">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="text-sm font-medium">
                                            {(selectedConversation.participant_names || selectedConversation.participant_usernames)?.join(' ↔ ')}
                                        </CardTitle>
                                        {selectedConversation.listing_title && (
                                            <CardDescription className="text-xs">
                                                Re: {selectedConversation.listing_title}
                                            </CardDescription>
                                        )}
                                    </div>
                                    <Button 
                                        variant="ghost" 
                                        size="sm"
                                        className="lg:size-icon"
                                        onClick={() => {
                                            setSelectedConversation(null);
                                            setMessages([]);
                                        }}
                                    >
                                        <ArrowLeft className="w-4 h-4 lg:hidden mr-1" />
                                        <span className="lg:hidden">Înapoi</span>
                                        <X className="w-4 h-4 hidden lg:block" />
                                    </Button>
                                </div>
                            </CardHeader>
                            <ScrollArea className="flex-1 p-4">
                                <div className="space-y-3">
                                    {messages.map(msg => (
                                        <div key={msg.message_id} className="flex gap-2">
                                            <Avatar className="w-8 h-8 shrink-0">
                                                {msg.sender_avatar ? (
                                                    <AvatarImage src={msg.sender_avatar} alt={msg.sender_name || msg.sender_username} />
                                                ) : null}
                                                <AvatarFallback className="text-xs">
                                                    {getInitials(msg.sender_name || msg.sender_username)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-sm">
                                                        {msg.sender_name || msg.sender_username}
                                                    </span>
                                                    <span className="text-xs text-muted-foreground">
                                                        {formatRelativeTime(msg.created_at)}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-foreground bg-muted/50 rounded-lg p-2 mt-1">
                                                    {msg.content}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </ScrollArea>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-muted-foreground">
                            <div className="text-center">
                                <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                                <p>Selectează o conversație pentru a vedea mesajele</p>
                            </div>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default AdminConversationsTab;
